import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@ygtechnologies/rubus-ui';
import { classnames, MarkdownDisplay } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faSwatchbook } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';

import theme from './RoomOptionTwoTierList.module.scss';
import { ProductCardList } from '@/seller/components/products/ProductCard/ProductCardList';
import { ConfiguratorGroupDto } from '@/services/configurator';
import { OptionPresetGroupType } from '@/services/option-preset/option-preset-group';
import { BuyerOptionCard } from '../../../BuyerOptionCard';
import { QuantityStepper } from '../../../BuyerOptionCard/QuantityStepper';
import { GroupQuantity } from './GroupQuantity';
import { useGroupQuantity } from './useGroupQuantity';
import { getVisibleBuyerOptions } from '../../../utils/visibleBuyerOptions';
import { useOptionConfigurator } from '../../../OptionConfiguratorContext';
import { useConfiguratorMutations } from '../../../ConfiguratorMutationContext';
import { useMobileActions } from '../../../MobileActionsContext';
import { useIsCompactChrome } from '../../../useConfiguratorLayout';
import {
    buildSelectedOptionIdByGroup,
    groupQuantityUnit,
    groupSelectedOption,
    inPredefinedOptionOrder,
    partitionOptionsByCompatibility,
    registerPredefinedOptionOrder,
} from './helpers';

interface GroupOptionDetailProps {
    group: ConfiguratorGroupDto;
    is3d: boolean;
    onClose: () => void;
}

export const GroupOptionDetail: React.FC<GroupOptionDetailProps> = ({ group, is3d, onClose }) => {
    const { isShowroom, immersive, rooms, configuratorData } = useOptionConfigurator();
    const { clearSelection } = useConfiguratorMutations();
    const { roomId } = useParams<{ roomId: string }>();
    const detailScrollRef = useRef<HTMLDivElement>(null);

    // Always render the *live* group from context, not the (possibly stale) snapshot passed in: in
    // the split layout the filters that recompute `filteredGroups` unmount while a group is open, so
    // the passed `group` wouldn't reflect a just-made selection. Context updates immediately (the
    // mutation patches the cache), so the chosen option marks selected right away.
    const liveGroup = useMemo(() => {
        const room = rooms.find((r) => r.roomId === Number(roomId));
        return room?.groups.find((g) => g.id === group.id) ?? group;
    }, [rooms, roomId, group]);

    // Current selection per group (across every deadline's groups) — the basis for deciding which of
    // this group's options conflict with the rest of the configuration.
    const selectedOptionIdByGroup = useMemo(
        () => buildSelectedOptionIdByGroup(configuratorData?.groups ?? []),
        [configuratorData]
    );

    // Capture the pre-defined option order for ALL groups from the loaded data (idempotent), so a
    // later mutation's refetch can't reshuffle the menu — including for groups not yet opened. Runs
    // before any selection can happen (mounting the detail requires opening a group first).
    useEffect(() => {
        registerPredefinedOptionOrder(configuratorData?.groups ?? []);
    }, [configuratorData]);

    const hasSelection = !!groupSelectedOption(liveGroup);
    // Buyer-editable quantity for the whole group (only for groups with allowManualQuantity, and
    // only once something is picked — the quantity rides along with the selection).
    const quantity = useGroupQuantity(liveGroup);
    // Pass the active room explicitly — the showroom mutation provider sits above the room route and
    // can't read roomId itself, so an omitted room would mis-target the stored selection.
    const handleClear = () =>
        clearSelection({
            groupId: liveGroup.id,
            roomId: roomId && roomId !== '0' ? Number(roomId) : null,
            // Clear against the group's own deadline (the URL deadline is vestigial in the
            // multi-deadline preview; identical to it in the single-deadline flow).
            deadlineId: liveGroup.deadlineId,
        });

    // On mobile the fixed detail footer is replaced by the shell's bottom action bar: register this
    // detail's Clear / Accept (close) actions there so the bar "switches" to them while a group is
    // open. Handlers are read through refs so the registration only re-runs when `hasSelection`
    // flips (not on every render), avoiding a set-state loop.
    // `setDetail` (a useState setter) is stable — depend on it, NOT the whole context value, which
    // changes on every `detail` update and would thrash this effect (clear → re-register loop).
    const setDetail = useMobileActions()?.setDetail;
    // With the compact chrome (<960px) the Confirm/Close footer moves to the shell's bottom action
    // bar; wider viewports keep the in-panel footer (they use the desktop chrome, not the bottom bar).
    const isMobile = useIsCompactChrome();
    const handleClearRef = useRef(handleClear);
    handleClearRef.current = handleClear;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        if (!setDetail || !isMobile) return;
        setDetail({
            hasSelection,
            onClear: () => handleClearRef.current(),
            onClose: () => onCloseRef.current(),
        });
        return () => setDetail(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setDetail, isMobile, hasSelection]);

    // On open, scroll the list to the current selection (with a little room above so the following
    // items stay visible). Key on group.id only — a data refetch after selecting gives a new group
    // object but the same id, so this won't re-run and yank the scroll around.
    useLayoutEffect(() => {
        const scrollEl = detailScrollRef.current;
        if (!scrollEl) return;

        // Match the reordered DOM: cards render valid-then-invalid in the pre-defined order.
        const options = inPredefinedOptionOrder(liveGroup.id, getVisibleBuyerOptions(liveGroup, isShowroom));
        const { valid, invalid } = partitionOptionsByCompatibility(options, selectedOptionIdByGroup, liveGroup.id);
        const ordered = [...valid, ...invalid];
        const selectedIndex = ordered.findIndex((o) => o.selected);
        if (selectedIndex < 0) {
            scrollEl.scrollTop = 0;
            return;
        }
        // BuyerOptionCard renders a Card with role="article"; match the selected one by render order.
        const cards = scrollEl.querySelectorAll('[role="article"]');
        const card = cards[selectedIndex] as HTMLElement | undefined;
        if (!card) {
            scrollEl.scrollTop = 0;
            return;
        }
        const delta = card.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top;
        // Bring the selection near the top (clamped by the browser) so 2+ following items show.
        scrollEl.scrollTop += delta - 8;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group.id]);

    const options = inPredefinedOptionOrder(liveGroup.id, getVisibleBuyerOptions(liveGroup, isShowroom));
    // Show the options that fit the current selection first, then the dependency-conflicting ones
    // below a separator (mirrors the immersive dependency wizard's valid/invalid split).
    const { valid: validOptions, invalid: invalidOptions } = partitionOptionsByCompatibility(
        options,
        selectedOptionIdByGroup,
        liveGroup.id
    );

    // When global style(s) are picked, the backend narrows a style-driven group to just the selected
    // style's products. Surface a hint so the shorter list isn't confusing — but only for groups that
    // styles actually affect (i.e. their options carry a picked style's preset).
    const pickedStyleNames = (configuratorData?.styleGroups ?? [])
        .filter((g) => g.type === OptionPresetGroupType.Main)
        .flatMap((g) => g.styles)
        .filter((s) => s.isPicked && options.some((o) => o.presets.some((p) => p.presetId === s.id)))
        .map((s) => s.name);
    const styleHint = pickedStyleNames.join(', ');
    const styleHintEl =
        pickedStyleNames.length > 0 ? (
            <div className={theme.styleHint}>
                <FontAwesomeIcon icon={faSwatchbook} className={theme.styleHintIcon} />
                <span>
                    <Trans>Only options matching your selected style ({styleHint}) are shown.</Trans>
                </span>
            </div>
        ) : null;

    return (
        <div className={classnames(theme.twoTier, theme.detail)}>
            <div className={theme.detailHeader}>
                <span className={theme.detailTitle} title={liveGroup.name}>
                    {liveGroup.name}
                </span>
                <GroupQuantity quantity={liveGroup.quantity} unit={groupQuantityUnit(liveGroup)} />
            </div>

            {/* Desktop: the hint stays fixed above the scrolling list. Mobile: it scrolls with the list
                (rendered inside the scroll area below) to save vertical space. */}
            {!isMobile && styleHintEl}

            {/* Only this middle region scrolls; the header and Accept footer stay fixed. */}
            <div ref={detailScrollRef} className={theme.detailScroll}>
                {isMobile && styleHintEl}
                {liveGroup.description && (
                    <section className={theme.description}>
                        <MarkdownDisplay markdown={liveGroup.description} />
                    </section>
                )}

                <ProductCardList compact={immersive} menuColumns>
                    {validOptions.map((option) => (
                        <BuyerOptionCard
                            key={option.id}
                            option={option}
                            isSelected={!!option.selected}
                            groupName={liveGroup.name}
                            is3d={is3d}
                        />
                    ))}
                </ProductCardList>

                {invalidOptions.length > 0 && (
                    <>
                        <div className={theme.optionSeparator}>
                            <span className={theme.separatorLabel}>
                                <FontAwesomeIcon className={theme.separatorIcon} icon={faCircleInfo} />
                                <span className={theme.deadlineName}>
                                    <Trans>Other options</Trans>
                                </span>
                            </span>
                        </div>
                        <p className={theme.optionSeparatorInfo}>
                            <Trans>
                                These options do not work with other already selected options. When you select one
                                of them, you will be asked to change other selections.
                            </Trans>
                        </p>
                        <ProductCardList compact={immersive} menuColumns>
                            {invalidOptions.map((option) => (
                                <BuyerOptionCard
                                    key={option.id}
                                    option={option}
                                    isSelected={!!option.selected}
                                    groupName={liveGroup.name}
                                    is3d={is3d}
                                />
                            ))}
                        </ProductCardList>
                    </>
                )}
            </div>

            {/* Desktop: the fixed footer holds the quantity stepper and the Close action. On mobile
                Clear/Close move to the shell's bottom action bar (registered above) so they aren't
                duplicated — but the stepper stays here, since the bar only carries the two actions. */}
            {(quantity.editable || !isMobile) && (
                <div className={theme.detailFooter}>
                    {quantity.editable && (
                        <div className={theme.quantityRow}>
                            <span className={theme.quantityLabel}>
                                <Trans>Quantity</Trans>
                            </span>
                            <QuantityStepper
                                value={quantity.value}
                                disabled={quantity.disabled}
                                onChange={quantity.commit}
                            />
                        </div>
                    )}
                    {/* Show exactly one action: Confirm once something is picked (the choice is already
                        persisted on click), otherwise Close as the escape. */}
                    {!isMobile &&
                        (hasSelection ? (
                            <Button variant="primary" size="sm" onClick={onClose}>
                                <Trans>Close</Trans>
                            </Button>
                        ) : (
                            <Button variant="tertiary" size="sm" onClick={onClose}>
                                <Trans>Close</Trans>
                            </Button>
                        ))}
                </div>
            )}
        </div>
    );
};
